import React from 'react';
import { Box, Chip, Stack, Typography, alpha, useTheme } from '@mui/material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import type {
  OperationProgressStep,
  OperationProgressStepStatus,
} from './operationProgress.types';
import { OperationProgressStateIcon } from './OperationProgressActivityList';

interface OperationProgressStepListProps {
  steps: OperationProgressStep[];
  compact: boolean;
  accentColor: string;
}

function getStepStatusLabel(status: OperationProgressStepStatus): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'current':
      return 'Running';
    case 'skipped':
      return 'Skipped';
    case 'error':
      return 'Stopped';
    default:
      return 'Pending';
  }
}

function getStepChipColor(status: OperationProgressStepStatus) {
  switch (status) {
    case 'error':
      return 'error' as const;
    case 'complete':
      return 'success' as const;
    case 'current':
      return 'primary' as const;
    case 'skipped':
      return 'warning' as const;
    default:
      return 'default' as const;
  }
}

export const OperationProgressStepList: React.FC<OperationProgressStepListProps> = ({
  steps,
  compact,
  accentColor,
}) => {
  const theme = useTheme();
  if (steps.length === 0) {
    return null;
  }

  const longList = steps.length > 4;
  return (
    <Stack
      spacing={layoutGap.comfort}
      data-testid="operation-progress-step-list"
      data-step-list-variant={longList ? 'vertical' : 'grid'}
    >
      {!longList ? (
        <Stack direction="row" spacing={layoutGap.comfort} alignItems="center" useFlexGap>
          {steps.map((step, index) => {
            const active = step.status === 'current';
            const complete = step.status === 'complete';
            const error = step.status === 'error';
            const color = error
              ? theme.palette.error.main
              : complete
                ? theme.palette.success.main
                : active
                  ? accentColor
                  : alpha(theme.palette.text.primary, 0.26);

            return (
              <React.Fragment key={step.id}>
                {index > 0 ? (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 10,
                      height: 1,
                      backgroundColor: alpha(theme.palette.divider, active || complete ? 0.95 : 0.72),
                    }}
                  />
                ) : null}
                <Box
                  sx={{
                    width: compact ? 9 : 10,
                    height: compact ? 9 : 10,
                    borderRadius: '50%',
                    backgroundColor: color,
                    boxShadow: active ? `0 0 0 4px ${alpha(accentColor, 0.12)}` : 'none',
                    flexShrink: 0,
                  }}
                  aria-label={`${step.label}: ${step.status}`}
                />
              </React.Fragment>
            );
          })}
        </Stack>
      ) : null}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: longList ? '1fr' : `repeat(${Math.min(steps.length, 4)}, minmax(0, 1fr))`,
          },
          gap: longList ? 0.55 : layoutGap.row,
        }}
      >
        {steps.map((step) => {
          const active = step.status === 'current';
          return (
            <Box
              key={step.id}
              aria-label={`${step.label}: ${step.status}`}
              sx={{
                minWidth: 0,
                ...(longList
                  ? {
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        sm: 'minmax(170px, 0.7fr) minmax(180px, 1.3fr) auto',
                      },
                      alignItems: 'center',
                      gap: { xs: 0.25, sm: 1 },
                      px: 0.85,
                      py: 0.55,
                      borderRadius: `${borderRadius.sm}px`,
                      border: `1px solid ${active ? alpha(accentColor, 0.25) : alpha(theme.palette.divider, 0.74)}`,
                      backgroundColor: active
                        ? alpha(accentColor, 0.055)
                        : alpha(theme.palette.background.paper, 0.44),
                    }
                  : {}),
              }}
            >
              <Stack direction="row" spacing={layoutGap.blend} alignItems="center" sx={{ minWidth: 0 }}>
                <OperationProgressStateIcon status={step.status} />
                <Typography
                  variant="caption"
                  sx={{
                    minWidth: 0,
                    fontWeight: active ? 750 : 650,
                    lineHeight: 1.25,
                    color: active ? 'text.primary' : 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {step.label}
                </Typography>
              </Stack>
              {step.detail && !compact ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    mt: longList ? 0 : layoutGap.micro,
                    lineHeight: 1.35,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {step.detail}
                </Typography>
              ) : null}
              {longList ? (
                <Chip
                  data-testid="operation-progress-step-status"
                  size="small"
                  variant="outlined"
                  label={getStepStatusLabel(step.status)}
                  color={getStepChipColor(step.status)}
                  sx={{
                    justifySelf: { xs: 'flex-start', sm: 'end' },
                    width: 78,
                    minWidth: 78,
                    height: 22,
                    mt: { xs: 0.35, sm: 0 },
                    '& .MuiChip-label': { fontWeight: 700 },
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};
